import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import { Building2, ArrowLeft, Loader2, Check } from 'lucide-react';

const CreateOrganization = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  const { notify } = useNotification();
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      notify.error('Organization name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await organizationsAPI.create(formData);
      notify.success(`${formData.name} created successfully!`);
      
      // Refresh user data to get updated org list
      await refreshUserData();
      
      // Navigate to org settings
      navigate('/organizations/settings');
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950 flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b border-slate-700 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Create Organization</h1>
            <p className="text-slate-400 mt-2">
              Set up a new workspace for your team
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Organization Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Acme Corporation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description <span className="text-slate-500">(optional)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                placeholder="Brief description of your organization..."
              />
            </div>

            {/* Info Box */}
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
              <h3 className="text-violet-300 font-medium mb-2">What happens next?</h3>
              <ul className="text-sm text-slate-400 space-y-1">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                  You'll become the owner of this organization
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                  Invite team members via email or add them directly
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                  All assessments and data will be shared within the org
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Building2 className="w-5 h-5" />
                  Create Organization
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
